import React from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import { evalCondition, notifyParent } from 'mailup-react-helpers'

import {
  Grid,
  GridCell,
  Panel,
  SpacingVertical,
  SPACING_4,
  SPACING_8,
  SPACING_16,
  TruncateAndTooltip,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_ITALIC_LIGHT,
  ProgressBarSmall,
  Spinner,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  LINK_VARIANT_EXTRALIGHT,
  Flex,
  FlexItem,
  Title,
  TITLE_VARIANT_M,
  Divider,
  DIVIDER_VARIANT_LIGHT
} from 'mailup-web-components'
import BoxThumbnail from './BoxThumbnail'
import styled from '@emotion/styled'

import { abTestState } from '../recoil/state/abTest'
import { pageState } from '../recoil/state/page'
import { getABTestResultCallback } from '../recoil/callbacks/callbacks'

import {
  ABTEST_STATUS_COMPLETED,
  ABTEST_STATUS_SUGGESTED_WINNER_DATE,
  ABTEST_STATUS_WINNER_MESSAGE_DEFINED
} from 'Api/beABTestApi'

const StyledTextResultDescr = styled(Text)`
  font-size: 13px !important;
  letter-spacing: 1px;
`

const StyledTextResult = styled(Text)`
  font-size: 18px;
`

const StyledChipBestValue = styled.span`
  position: relative;
  top: -3px;
  margin-left: 6px;
  font-size: 13px;
  padding: 2px 6px;
  border-radius: 2px;
  color: #16a7cd;
  background-color: #e2f2f7;
`

const StyledTextBetterValue = styled.span`
  font-size: 13px;
  padding-left: 6px;
  padding-bottom: 6px;
  font-style: italic;
  color: #a8a8a8 !important;
`

const AbTestMessages = () => {
  const intl = useIntl()
  const {
    Info,
    Messages,
    maxOpenRate,
    maxClickRate,
    maxAbuseRate,
    testNotStartMode
  } = useRecoilValue(abTestState)
  const { loadingResult } = useRecoilValue(pageState)
  const userHasStats = evalCondition('uo:STAT')
  const getABTestResult = useRecoilCallback(getABTestResultCallback, [])

  function getDescMsg(item) {
    let ret =
      intl.formatMessage({ id: 'Load.MailupCommon.Id' }) +
      ' ' +
      item.IdMessaggio
    if (item.Note) {
      ret += ' - ' + item.Note
    }
    return ret
  }

  const goToStats = idMessage => {
    notifyParent('redirectMainFrame', [
      `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${idMessage}`
    ])
  }

  const updateResults = () => {
    getABTestResult(Info.ABTestID)
  }

  return (
    <Panel collapsible={false}>
      {Messages.filter(m => m.TipoInvio === 1).map((item, i) => (
        <div key={i}>
          {i > 0 && (
            <>
              <SpacingVertical variant={SPACING_16} />
              <Divider variant={DIVIDER_VARIANT_LIGHT} />
              <SpacingVertical variant={SPACING_16} />
            </>
          )}

          <Flex>
            <FlexItem width='90px' justifySelf='flex-start'>
              <BoxThumbnail item={item} />
            </FlexItem>
            <FlexItem
              padding='0 0 0 24px'
              justifySelf='flex-end'
              width={
                testNotStartMode ? 'calc(100% - 90px)' : 'calc(40% - 45px)'
              }
              is100={testNotStartMode}
            >
              <SpacingVertical variant={SPACING_4} />
              <Text variant={TEXT_VARIANT_BOLD}>
                <TruncateAndTooltip text={item.Subject} />
              </Text>
              <SpacingVertical variant={SPACING_4} />
              <Text variant={TEXT_VARIANT_LIGHT}>
                <TruncateAndTooltip text={getDescMsg(item)} />
              </Text>

              <SpacingVertical variant={SPACING_8} />

              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({ id: 'Load.MailupCommon.Recipients' })}{' '}
                {item ? intl.formatNumber(item.Queued) : <Spinner />}
              </Text>

              <SpacingVertical variant={SPACING_8} />
              <ProgressBarSmall
                perc={intl.formatNumber(
                  Info.PercTest /
                    Messages.filter(m => m.TipoInvio === 1).length,
                  {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0
                  }
                )}
              />
            </FlexItem>
            {!testNotStartMode && (
              <>
                <FlexItem
                  padding='0 0 0 24px'
                  justifySelf='flex-end'
                  width='calc(60% - 45px)'
                >
                  <Grid cols={3}>
                    <GridCell>
                      <SpacingVertical variant={SPACING_4} />
                      {loadingResult && (
                        <Text variant={TEXT_VARIANT_LIGHT}>
                          <Spinner />
                        </Text>
                      )}
                      {!loadingResult && (
                        <>
                          {userHasStats && (
                            <Title variant={TITLE_VARIANT_M}>
                              <Link
                                onClick={() => goToStats(item.IdMessaggio)}
                                variant={LINK_VARIANT_HIGHLIGHT}
                                data-mu-test='abTestManage-OpenRate'
                              >
                                {intl.formatNumber(item.OpenRate)}%
                              </Link>
                            </Title>
                          )}
                          {!userHasStats && (
                            <StyledTextResult
                              htmlElement='span'
                              variant={TEXT_VARIANT_BOLD}
                            >
                              {intl.formatNumber(item.OpenRate)}%
                            </StyledTextResult>
                          )}

                          {item.DeltaOpenRate > 0 && (
                            <>
                              {item.OpenRate === maxOpenRate ? (
                                <StyledChipBestValue>
                                  +{intl.formatNumber(item.DeltaOpenRate)}
                                </StyledChipBestValue>
                              ) : (
                                <StyledTextBetterValue>
                                  +{intl.formatNumber(item.DeltaOpenRate)}
                                </StyledTextBetterValue>
                              )}
                            </>
                          )}
                          {Info.TipoTest !== 'A' &&
                            item.DeltaOpenRate === -1 &&
                            item.OpenRate === maxOpenRate &&
                            maxOpenRate > 0 && (
                              <StyledChipBestValue>
                                {intl.formatMessage({
                                  id: 'Load.AbTest.ResultDeltaNotANumber'
                                })}
                              </StyledChipBestValue>
                            )}
                        </>
                      )}
                      <StyledTextResultDescr variant={TEXT_VARIANT_LIGHT}>
                        {intl.formatMessage({
                          id: 'Load.Statistics.PercOpens'
                        })}
                      </StyledTextResultDescr>
                    </GridCell>
                    <GridCell>
                      <SpacingVertical variant={SPACING_4} />
                      {loadingResult && (
                        <Text variant={TEXT_VARIANT_LIGHT}>
                          <Spinner />
                        </Text>
                      )}
                      {!loadingResult && (
                        <>
                          {userHasStats && (
                            <Title variant={TITLE_VARIANT_M}>
                              <Link
                                onClick={() => goToStats(item.IdMessaggio)}
                                variant={LINK_VARIANT_HIGHLIGHT}
                                data-mu-test='abTestManage-OpenRate'
                              >
                                {intl.formatNumber(item.ClickRate)}%
                              </Link>
                            </Title>
                          )}
                          {!userHasStats && (
                            <StyledTextResult variant={TEXT_VARIANT_BOLD}>
                              {intl.formatNumber(item.ClickRate)}%
                            </StyledTextResult>
                          )}
                          {item.DeltaClickRate > 0 && (
                            <>
                              {item.ClickRate === maxClickRate ? (
                                <StyledChipBestValue>
                                  +{intl.formatNumber(item.DeltaClickRate)}
                                </StyledChipBestValue>
                              ) : (
                                <StyledTextBetterValue>
                                  +{intl.formatNumber(item.DeltaClickRate)}
                                </StyledTextBetterValue>
                              )}
                            </>
                          )}
                          {Info.TipoTest !== 'A' &&
                            item.DeltaClickRate === -1 &&
                            item.ClickRate === maxClickRate &&
                            maxClickRate > 0 && (
                              <StyledChipBestValue>
                                {intl.formatMessage({
                                  id: 'Load.AbTest.ResultDeltaNotANumber'
                                })}
                              </StyledChipBestValue>
                            )}
                        </>
                      )}
                      <StyledTextResultDescr variant={TEXT_VARIANT_LIGHT}>
                        {intl.formatMessage({
                          id: 'Load.Statistics.PercClicks'
                        })}
                      </StyledTextResultDescr>
                    </GridCell>
                    <GridCell>
                      <SpacingVertical variant={SPACING_4} />
                      {loadingResult && (
                        <Text variant={TEXT_VARIANT_LIGHT}>
                          <Spinner />
                        </Text>
                      )}
                      {!loadingResult && (
                        <>
                          {userHasStats && (
                            <Title variant={TITLE_VARIANT_M}>
                              <Link
                                onClick={() => goToStats(item.IdMessaggio)}
                                variant={LINK_VARIANT_HIGHLIGHT}
                                data-mu-test='abTestManage-OpenRate'
                              >
                                {intl.formatNumber(item.AbuseRate)}%
                              </Link>
                            </Title>
                          )}
                          {!userHasStats && (
                            <StyledTextResult variant={TEXT_VARIANT_BOLD}>
                              {intl.formatNumber(item.AbuseRate)}%
                            </StyledTextResult>
                          )}
                          {item.DeltaAbuseRate > 0 && (
                            <>
                              {item.AbuseRate === maxAbuseRate ? (
                                <StyledChipBestValue>
                                  +{intl.formatNumber(item.DeltaAbuseRate)}
                                </StyledChipBestValue>
                              ) : (
                                <StyledTextBetterValue>
                                  +{intl.formatNumber(item.DeltaAbuseRate)}
                                </StyledTextBetterValue>
                              )}
                            </>
                          )}
                          {Info.TipoTest !== 'A' &&
                            item.DeltaAbuseRate === -1 &&
                            item.AbuseRate === maxAbuseRate &&
                            maxAbuseRate > 0 && (
                              <StyledChipBestValue>
                                {intl.formatMessage({
                                  id: 'Load.AbTest.ResultDeltaNotANumber'
                                })}
                              </StyledChipBestValue>
                            )}
                        </>
                      )}
                      <StyledTextResultDescr variant={TEXT_VARIANT_LIGHT}>
                        {intl.formatMessage({
                          id: 'Load.Statistics.AbuseReportingRate'
                        })}
                      </StyledTextResultDescr>
                    </GridCell>
                  </Grid>
                </FlexItem>
              </>
            )}
          </Flex>
        </div>
      ))}
      {!testNotStartMode && (
        <>
          <SpacingVertical variant={SPACING_16} />
          <Divider variant={DIVIDER_VARIANT_LIGHT} />

          <SpacingVertical variant={SPACING_16} />

          <Flex alignItems='center' justifyContent='center' width='100%'>
            {Info.TipoTest !== 'A' &&
            [
              ABTEST_STATUS_COMPLETED,
              ABTEST_STATUS_WINNER_MESSAGE_DEFINED,
              ABTEST_STATUS_SUGGESTED_WINNER_DATE
            ].indexOf(Info.Stato) >= 0 ? (
              <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
                {intl.formatMessage({
                  id: 'Load.AbTest.StatisticInfoAutoTestFinished'
                })}
              </Text>
            ) : (
              <>
                <FlexItem padding='0 2px 0 0'>
                  <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement='span'>
                    {intl.formatMessage({ id: 'Load.AbTest.ManageTestInfo' })}
                  </Text>
                </FlexItem>
                <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement='span'>
                  <Link
                    onClick={updateResults}
                    variant={LINK_VARIANT_EXTRALIGHT}
                  >
                    {intl.formatMessage({ id: 'Load.AbTest.UpdateResults' })}
                  </Link>
                </Text>
              </>
            )}
          </Flex>
        </>
      )}
    </Panel>
  )
}
export default AbTestMessages
